import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { competitors, reachlyVsMatrix, faqsPerCompetitor } from '../mock/mock';
import { Check, X, ArrowRight, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

export function CompareHub() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return (
    <div className="bg-[#0a0a0b] text-zinc-100">
      <SEO
        title="Reachly comparisons — best outreach tool for job seekers in 2026"
        description="Compare Reachly with Apollo, ZoomInfo, Lusha, RocketReach, Hunter, Kaspr, and ContactOut. Features, pricing, and which is best for job-search cold outreach."
        canonical={`${origin}/compare`}
        jsonLd={{
          '@context':'https://schema.org','@type':'BreadcrumbList',
          itemListElement: [
            { '@type':'ListItem', position:1, name:'Home', item: `${origin}/` },
            { '@type':'ListItem', position:2, name:'Compare', item: `${origin}/compare` },
          ]
        }}
      />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-20 pb-8">
        <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Comparisons</div>
        <h1 className="mt-3 font-display text-5xl md:text-6xl">Reachly vs the alternatives.</h1>
        <p className="mt-5 text-zinc-400 max-w-2xl text-lg">
          Honest, side-by-side comparisons with the most-searched outreach and contact-finder tools — so you can pick the right one for your job search.
        </p>
      </section>
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-24">
        <div className="grid md:grid-cols-2 gap-4">
          {competitors.map(c => (
            <Link key={c.slug} to={`/compare/${c.slug}`}>
              <Card className="bg-[#111110] border-zinc-900 hover:border-[#3a2f18] transition-colors p-6 h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-2xl">Reachly vs {c.name}</div>
                    <div className="text-sm text-zinc-500 mt-1">{c.tagline}</div>
                  </div>
                  <ChevronRight size={18} className="text-[#d4a94a]"/>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-zinc-900 text-zinc-300 border border-zinc-800 font-normal">{c.pricing}</Badge>
                  {c.freeOption && <Badge className="bg-emerald-950 text-emerald-300 border border-emerald-900 font-normal">Free tier</Badge>}
                </div>
                <div className="mt-4 text-sm text-zinc-400">Best for: {c.bestFor}</div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ComparePage() {
  const { competitor } = useParams();
  const comp = competitors.find(c => c.slug === competitor);
  if (!comp) return <Navigate to="/compare" replace />;
  const r = reachlyVsMatrix.reachly;
  const faqs = faqsPerCompetitor(comp.name);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}/compare/${comp.slug}`;
  const title = `Reachly vs ${comp.name}: features, pricing and which is better in 2026`;
  const description = `An honest comparison of Reachly and ${comp.name} across features, pricing, ease of use, and best fit. Which is better for job-search cold outreach in 2026?`;

  return (
    <div className="bg-[#0a0a0b] text-zinc-100">
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        jsonLd={[
          { '@context':'https://schema.org','@type':'BreadcrumbList', itemListElement:[
            { '@type':'ListItem', position:1, name:'Home', item:`${origin}/` },
            { '@type':'ListItem', position:2, name:'Compare', item:`${origin}/compare` },
            { '@type':'ListItem', position:3, name:`Reachly vs ${comp.name}`, item: canonical },
          ]},
          { '@context':'https://schema.org','@type':'FAQPage', mainEntity: faqs.map(f => ({ '@type':'Question', name:f.q, acceptedAnswer:{ '@type':'Answer', text:f.a } })) },
          { '@context':'https://schema.org','@type':'Product', name:'Reachly', description:'Cold outreach for job seekers', brand:{ '@type':'Brand', name:'Reachly' }, offers:{ '@type':'Offer', price:'0', priceCurrency:'USD' } }
        ]}
      />

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-14">
        <nav className="text-xs text-zinc-500"><Link to="/" className="hover:text-[#f5d67a]">Home</Link> / <Link to="/compare" className="hover:text-[#f5d67a]">Compare</Link> / <span className="text-zinc-400">Reachly vs {comp.name}</span></nav>
        <div className="mt-4 text-sm uppercase tracking-widest text-[#d4a94a]">Alternatives · 2026</div>
        <h1 className="mt-3 font-display text-4xl md:text-6xl leading-tight">Reachly vs {comp.name}: <span className="gold-text">which wins in 2026?</span></h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-3xl">
          {comp.name} is a strong tool for {comp.bestFor.toLowerCase()}. Reachly is built specifically for job-search outreach: multi-step sequences to recruiters, Outlook-native sending, and CV-based contact import. Here's how they stack up, feature by feature.
        </p>
        <div className="mt-8 flex gap-3">
          <Button asChild className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"><Link to="/signup">Try Reachly free <ArrowRight size={14} className="ml-1"/></Link></Button>
          <Button asChild variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900"><Link to="/compare">See all comparisons</Link></Button>
        </div>
      </section>

      {/* Side by side */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
        <h2 className="font-display text-3xl">Feature-by-feature</h2>
        <Card className="mt-6 bg-[#111110] border-zinc-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase">
              <tr className="border-b border-zinc-900">
                <th className="text-left px-5 py-4 font-medium">Feature</th>
                <th className="text-left px-5 py-4 font-medium text-[#f5d67a]">Reachly</th>
                <th className="text-left px-5 py-4 font-medium">{comp.name}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {[
                ['Pricing', r.price, comp.pricing],
                ['Free option', r.freeOption ? 'Yes' : 'No', comp.freeOption ? 'Yes' : 'No'],
                ['Campaign sequences', r.campaignSequences, comp.slug==='hunter' ? 'Basic (Hunter Campaigns)' : comp.slug==='apollo' ? 'Yes' : 'Limited'],
                ['Follow-up automation', r.followupAutomation, comp.slug==='apollo' ? 'Yes' : 'Manual'],
                ['Reply detection', r.replyDetection, 'Basic'],
                ['Native Outlook sending', 'Yes', comp.slug==='apollo' || comp.slug==='hunter' ? 'Yes' : 'No'],
                ['AI CV parsing', r.cvParsing, 'No'],
                ['CSV contact import', 'Yes', 'Yes'],
                ['Best for', r.bestFor, comp.bestFor],
              ].map((row, i) => (
                <tr key={row[0]}>
                  <td className="px-5 py-4 text-zinc-400">{row[0]}</td>
                  <td className="px-5 py-4 text-zinc-100 font-medium">{row[1]}</td>
                  <td className="px-5 py-4 text-zinc-300">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>

      {/* Pros / cons */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-4">
        <h2 className="font-display text-3xl">Pros and cons</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <Card className="bg-[#111110] border-zinc-900 p-6">
            <div className="font-display text-xl gold-text">Reachly</div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 tracking-wider">Strengths</div>
              <ul className="mt-2 space-y-2">
                {['Purpose-built for job-search outreach','Native Outlook OAuth — sends from your inbox','AI-powered CV & CSV parsing','Auto follow-ups pause on reply','Free forever plan'].map(s => (
                  <li key={s} className="flex gap-2 text-sm text-zinc-200"><Check size={16} className="text-emerald-400 mt-0.5 shrink-0"/>{s}</li>
                ))}
              </ul>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 tracking-wider">Trade-offs</div>
              <ul className="mt-2 space-y-2">
                {['Focused on individuals, not sales teams','No massive B2B database (bring your own contacts)'].map(s => (
                  <li key={s} className="flex gap-2 text-sm text-zinc-400"><X size={16} className="text-zinc-500 mt-0.5 shrink-0"/>{s}</li>
                ))}
              </ul>
            </div>
          </Card>
          <Card className="bg-[#111110] border-zinc-900 p-6">
            <div className="font-display text-xl">{comp.name}</div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 tracking-wider">Strengths</div>
              <ul className="mt-2 space-y-2">
                {comp.keyStrengths.map(s => <li key={s} className="flex gap-2 text-sm text-zinc-200"><Check size={16} className="text-emerald-400 mt-0.5 shrink-0"/>{s}</li>)}
              </ul>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 tracking-wider">Trade-offs</div>
              <ul className="mt-2 space-y-2">
                {comp.weaknesses.map(s => <li key={s} className="flex gap-2 text-sm text-zinc-400"><X size={16} className="text-zinc-500 mt-0.5 shrink-0"/>{s}</li>)}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* Verdict */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
        <Card className="bg-gradient-to-b from-[#1a1613] to-[#141110] border-[#3a2f18] p-8">
          <div className="font-display text-3xl">The honest verdict</div>
          <p className="mt-4 text-zinc-300 leading-relaxed">
            If you're running a sales operation and need {comp.name.split(' ')[0]}'s scale, {comp.name} is the right pick — no contest. But if you're a job seeker sending targeted outreach to recruiters and hiring managers, Reachly's workflow beats {comp.name} on the details that matter: your emails come from your Outlook, follow-ups pause the moment someone replies, and CV import gets you from zero to a queued campaign in minutes. And it's free to start.
          </p>
          <div className="mt-6 flex gap-3">
            <Button asChild className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"><Link to="/signup">Start Reachly free <ArrowRight size={14} className="ml-1"/></Link></Button>
          </div>
        </Card>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
        <h2 className="font-display text-3xl">Frequently asked</h2>
        <Accordion type="single" collapsible className="mt-6">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`i${i}`} className="border-zinc-900">
              <AccordionTrigger className="text-left text-zinc-100 hover:text-[#f5d67a]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Related */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
        <div className="font-display text-2xl">Other comparisons</div>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          {competitors.filter(c => c.slug !== comp.slug).slice(0,6).map(c => (
            <Link key={c.slug} to={`/compare/${c.slug}`} className="rounded-lg border border-zinc-900 hover:border-[#3a2f18] p-4 flex items-center justify-between transition-colors">
              <span className="text-sm text-zinc-200">Reachly vs {c.name}</span>
              <ChevronRight size={14} className="text-[#d4a94a]"/>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
